import React, { useCallback, useEffect, useState } from 'react';
import { debounce } from 'lodash';
import { AsyncSelectForm } from '../../components/select-form/select-form';
import { components } from 'react-select';
import { Typography } from '../../components/typography/typography';
import Icon from '../../components/icons/icon';
import classNames from 'classnames';
import { formatPhoneNumber } from '../../util/util';
import { Vendor } from '../../clients/quipli-service/vendor/vendor';
import { useVendorApi } from '../../clients/quipli-service/vendor/vendor-api';
import { OrderSection } from '../order/order-section/order-section';
import { Dropdown } from '../../components/dropdown/dropdown';
import { Button } from '../../components/form/button';
import { QuipliLink } from '../../components/link/quipli-link';
import { CustomerAddressList } from '../order/create/customer-info';
import { useAPI } from '../../clients/quipli-service/quipli-service-client';
import { UserAddress } from '../../clients/quipli-service/user/user';

export const VendorOrderSelect = ({
  vendor,
  setVendor,
  canRemoveVendor = true,
  isEditMode = false,
  className,
}: {
  vendor?: Vendor;
  setVendor?: (c?: Vendor) => void;
  canRemoveVendor?: boolean;
  isEditMode?: boolean;
  className?: string;
}) => {
  return (
    <div className={classNames(className)}>
      {vendor ? (
        <VendorInfo vendor={vendor} setVendor={setVendor} canRemoveVendor={canRemoveVendor} isEditMode={isEditMode} />
      ) : (
        <OrderSection className="flex flex-col gap-[12px]" header="Vendor">
          <SelectVendor vendor={vendor} setVendor={setVendor} />
        </OrderSection>
      )}
    </div>
  );
};

export const SelectVendor = ({
  vendor,
  setVendor,
  isClearable = false,
}: {
  vendor?: Vendor;
  setVendor?: (c?: Vendor) => void;
  isClearable?: boolean;
}) => {
  const { searchVendors } = useVendorApi();

  // Callbacks
  const search = useCallback(
    debounce((keyword: string, callback) => {
      searchVendors({
        keyword,
        pageNumber: 1,
        pageSize: 30,
      }).then((res) => callback(res.items));
    }, 300),
    [],
  );

  const onSelected = (v?: Vendor) => {
    setVendor?.(v);
  };

  return (
    <AsyncSelectForm<Vendor>
      cacheOptions
      defaultOptions
      loadOptions={search}
      getOptionLabel={(op) => op.name}
      getOptionValue={(op) => `${op.id}`}
      onChange={(value) => onSelected(value || undefined)}
      loadingMessage={() => 'Loading vendors...'}
      noOptionsMessage={() => 'No vendors found'}
      dataCy="customer-select"
      value={vendor}
      isClearable={isClearable}
      components={{
        Placeholder: (props) => (
          <components.Placeholder {...props}>
            <Typography type="text-sm">Search vendors</Typography>
          </components.Placeholder>
        ),
        ValueContainer: (props) => (
          <div className="flex items-center gap-[6px] pl-[10px]">
            <Icon icon="Search" size="medium" stroke="var(--grey-500)" />
            <components.ValueContainer {...props} />
          </div>
        ),
        Option: (props) => {
          //@ts-expect-error __isNew__
          const isCreateOption = props.data.__isNew__ === true;

          return (
            <components.Option {...props}>
              <div
                className={classNames('flex items-center gap-[6px]', {
                  'justify-between': !isCreateOption,
                })}
              >
                <div className="flex flex-col gap-[4px]">
                  <Typography type="text-md" weight="medium" className="text-grey-900">
                    {props.children}
                  </Typography>
                  <div>
                    <Typography type="text-sm" className="text-grey-500">
                      {formatPhoneNumber(props.data.phone)}
                    </Typography>
                    <Typography type="text-sm" className="text-grey-500">
                      {props.data.email}
                    </Typography>
                  </div>
                </div>
                {props.isSelected && <Icon icon="Check" stroke="var(--quipli-black-800)" />}
              </div>
            </components.Option>
          );
        },
      }}
    />
  );
};

const VendorInfo = ({
  vendor,
  setVendor,
  canRemoveVendor = false,
  isEditMode = false,
}: {
  vendor: Vendor;
  setVendor?: (v?: Vendor) => void;
  canRemoveVendor?: boolean;
  isEditMode?: boolean;
  orderId?: number;
}) => {
  const { getUserAddresses } = useAPI();

  const [address, setAddress] = useState<UserAddress>();
  const [isAddressLoading, setIsAddressLoading] = useState<boolean>(false);

  const fetchVendorAddress = async () => {
    setIsAddressLoading(true);
    try {
      const addresses = await getUserAddresses(vendor.id);
      setAddress(addresses.length > 0 ? addresses[0] : undefined);
    } catch (e) {
      // Do error handling
    } finally {
      setIsAddressLoading(false);
    }
  };

  useEffect(() => {
    fetchVendorAddress();
  }, []);

  return (
    <>
      <div className="flex flex-col gap-y-[12px]">
        <OrderSection
          className="flex flex-col gap-[8px]"
          header={
            <div className="flex justify-between items-start">
              <Typography type="text-md" weight="medium" className="text-grey-800">
                Vendor
              </Typography>
              {canRemoveVendor && (
                <div>
                  <Dropdown
                    trigger={() => (
                      <Button className="button-tertiary">
                        <Icon icon="DotsHorizontal" />
                      </Button>
                    )}
                  >
                    <div onClick={() => setVendor?.(undefined)}>
                      <Typography type="text-sm" className="text-error-400">
                        Remove vendor
                      </Typography>
                    </div>
                  </Dropdown>
                </div>
              )}
            </div>
          }
        >
          <QuipliLink target="_blank" to={`vendors/${vendor.id}`} className="text-link-200 underline w-fit">
            <Typography type="text-sm">{vendor.name}</Typography>
          </QuipliLink>

          {(vendor.email || vendor.phone) && (
            <div className="border-t border-solid border-x-0 border-b-0 border-t-grey-200 pt-[12px]">
              <Typography type="text-md" weight="medium" className="text-grey-800">
                Contact Information
              </Typography>

              <div className="pt-[8px] flex flex-col gap-y-[8px]">
                {vendor.email && (
                  <QuipliLink target="_blank" to={`vendors/${vendor.id}`} className="text-link-200 underline w-fit">
                    <Typography type="text-sm">{vendor.email}</Typography>
                  </QuipliLink>
                )}
                {vendor.phone && (
                  <Typography type="text-sm" className="text-grey-800">
                    {formatPhoneNumber(vendor.phone)}
                  </Typography>
                )}
              </div>
            </div>
          )}

          <CustomerAddressList
            customer={vendor}
            title="Address"
            type="delivery"
            isLoading={isAddressLoading}
            setUnsavedAddresses={() => {}}
            list={address ? [address] : []}
            fetchList={fetchVendorAddress}
            address={address}
            setAddress={setAddress}
            isEditing={isEditMode}
            canAdd={!address}
            canSelect={false}
            hideAddressType
            hideSaveForFutureUse
          />
        </OrderSection>
      </div>
    </>
  );
};
